import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { FaSearch } from 'react-icons/fa';
import { searchReports } from '../services/api';
import { useUserGroups } from '../hooks/useUserGroups';
import { useAppRouter } from '../hooks/useAppRouter';
import '../pages/shared/Dashboard.css';

interface ReportSearchResult {
  displayname: string;
  category: string;
  subcategory: string;
  reportid: string;
  workspaceid: string;
  report?: string;
  workspacename?: string;
  type?: string;
  id?: number;
  azureSecurityGroupName?: string;
}

const ReportSearch: React.FC = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState<ReportSearchResult[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const { groups: userGroups, loading: groupsLoading } = useUserGroups();
  const { updateSearchParams, setSearchParams } = useAppRouter();
  const navigate = useNavigate();
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  
  // Get Redux state for tabs
  const openTabs = useSelector((state: any) => state.tabs.openTabs);
  const activeTab = useSelector((state: any) => state.tabs.activeTab);
  const tabReportInfo = useSelector((state: any) => state.tabs.tabReportInfo);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced search
  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    if (!searchQuery.trim() || groupsLoading) {
      setResults([]);
      setIsOpen(false);
      return;
    }

    debounceTimer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const data = await searchReports(searchQuery.trim(), userGroups);
        setResults(Array.isArray(data) ? data : []);
        setIsOpen(data && Array.isArray(data) && data.length > 0);
      } catch (error) {
        console.error('Search error:', error);
        setResults([]);
        setIsOpen(false);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [searchQuery, userGroups, groupsLoading]);

  const handleResultClick = (result: ReportSearchResult) => {
    // Create tab ID and check if tab is already open
    const tabId = `${result.workspaceid}-${result.reportid}`;
    const isOpen = openTabs.includes(tabId);
    
    // Build URL params
    const params = new URLSearchParams();
    params.set('department', result.category);
    params.set('sub-department', result.subcategory);
    params.set('workspaceId', result.workspaceid);
    params.set('reportId', result.reportid);
    
    // Navigate to reports page with params included
    const reportsPath = `/reports?${params.toString()}`;
    if (window.location.pathname !== '/reports') {
      navigate(reportsPath);
    } else {
      // If already on reports page, just update the params
      setSearchParams(params);
    }
    
    if (isOpen) {
      // If tab is already open, just switch to it
      if (activeTab !== tabId) {
        dispatch({ type: 'tabs/setActiveTab', payload: tabId });
      }
    } else {
      // Add new tab and set report info
      dispatch({ type: 'tabs/addTab', payload: tabId });
      dispatch({ type: 'tabs/setTabReportInfo', payload: { ...tabReportInfo, [tabId]: result } });
      dispatch({ type: 'tabs/setActiveTab', payload: tabId });
    }
    
    setSearchQuery('');
    setIsOpen(false);
  };

  const handleInputFocus = () => {
    if (results.length > 0) {
      setIsOpen(true);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  return (
    <div ref={searchRef} className="report-search-container" style={{ position: 'relative', marginRight: '16px' }}>
      <div className="report-search-input-wrapper" style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
        <span style={{ position: 'absolute', left: '12px', color: '#666', fontSize: '14px' }}>{FaSearch({})}</span>
        <input
          ref={inputRef}
          type="text"
          className="report-search-input"
          placeholder={t('reports.searchReports')}
          value={searchQuery}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          style={{
            padding: '8px 12px 8px 36px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            fontSize: '14px',
            width: '300px',
            outline: 'none',
          }}
        />
        {loading && (
          <span style={{ position: 'absolute', right: '12px', color: '#666', fontSize: '12px' }}>
            {t('reports.searching')}
          </span>
        )}
      </div>
      
      {isOpen && results.length > 0 && (
        <div
          className="report-search-results"
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            marginTop: '4px',
            backgroundColor: 'white',
            border: '1px solid #ddd',
            borderRadius: '4px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            maxHeight: '400px',
            overflowY: 'auto',
            zIndex: 1000,
            width: '100%',
          }}
        >
          {results.map((result, index) => (
            <div
              key={`${result.workspaceid}-${result.reportid}-${index}`}
              onClick={() => handleResultClick(result)}
              style={{
                padding: '12px 16px',
                cursor: 'pointer',
                borderBottom: index < results.length - 1 ? '1px solid #eee' : 'none',
                transition: 'background-color 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f5f5f5';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'white';
              }}
            >
              <div style={{ fontWeight: 600, fontSize: '14px', color: '#222', marginBottom: '4px' }}>
                {result.displayname}
              </div>
              <div style={{ fontSize: '12px', color: '#666' }}>
                {result.category} / {result.subcategory}
              </div>
            </div>
          ))}
        </div>
      )}
      
      {isOpen && !loading && searchQuery.trim() && results.length === 0 && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            marginTop: '4px',
            backgroundColor: 'white',
            border: '1px solid #ddd',
            borderRadius: '4px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            padding: '16px',
            textAlign: 'center',
            color: '#666',
            zIndex: 1000,
            width: '100%',
          }}
        >
          {t('reports.noReportsFound')}
        </div>
      )}
    </div>
  );
};

export default ReportSearch;

